import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  useSearchTimeExtendedExamEventSessionExamineesQuery,
  type ExamEventSession,
} from '@exam-center/graphql';
import { formatStartToEndTime } from '@exam-center/utils';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ErrorState,
  Flex,
  Text,
  SearchInput,
  NoRowsOverlay,
} from '@shared/components';
import { useMemo, useState, useEffect } from 'react';

import { TimeExtensionHistoryDataGrid, EmptyTimeExtensionHistoryOverlay } from '../../components';
import { getAdjustedSessionTime } from '../../utils';

import { mapTimeExtendedExamEventSessionExamineesToTimeExtensionHistoryRows } from './utils';

export interface TimeExtensionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export default function TimeExtensionHistoryModal(props: Readonly<TimeExtensionHistoryModalProps>) {
  const { isOpen, onClose, sessionId } = props;
  const { examEvent } = useExamEventDetails();
  const [searchParams, setSearchParams] = useState({ search: '', page: 0, pageSize: 10 });
  const open = isOpen && !!sessionId;

  const [selectedSession, setSelectedSession] = useState<ExamEventSession | null>(null);

  useEffect(() => {
    if (examEvent.sessions.length > 0 && sessionId) {
      const foundSession = examEvent.sessions.find((session) => session.id === sessionId);
      setSelectedSession(foundSession ?? null);
    }
  }, [examEvent.sessions, sessionId]);

  const adjustedSessionTime = useMemo(() => {
    if (selectedSession) {
      return getAdjustedSessionTime(selectedSession, examEvent.postponeStartDurationInMs ?? 0);
    }
    return null;
  }, [selectedSession, examEvent.postponeStartDurationInMs]);

  const sessionText = useMemo(
    () =>
      selectedSession
        ? examEvent.sessions.length > 1
          ? `ช่วงสอบที่ ${selectedSession.round}`
          : 'ช่วงสอบ'
        : '',
    [examEvent.sessions.length, selectedSession],
  );

  const handleSearchChange = (value: string) => {
    setSearchParams((prev) => ({ ...prev, search: value, page: 0 }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchParams((prev) => ({ ...prev, pageSize }));
  };

  const {
    data: searchExamineesData,
    previousData: previousSearchExamineesData,
    loading: isLoading,
  } = useSearchTimeExtendedExamEventSessionExamineesQuery({
    variables: {
      searchTimeExtendedExamEventSessionExamineeInput: {
        sessionId,
        examEventId: examEvent.id,
        searchExaminee: {
          filter: {
            searchText: {
              value: searchParams.search,
              fields: ['fullName'],
            },
          },
        },
        paging: {
          pageSize: searchParams.pageSize,
          currentPage: searchParams.page,
        },
      },
    },
    skip: !sessionId || !isOpen,
  });

  const examinees = useMemo(
    () =>
      searchExamineesData
        ? mapTimeExtendedExamEventSessionExamineesToTimeExtensionHistoryRows(
            searchExamineesData?.ecSearchTimeExtendedExamEventSessionExaminees
              .extendedExamEventSessionExaminees,
            examEvent.courses,
          )
        : previousSearchExamineesData
          ? mapTimeExtendedExamEventSessionExamineesToTimeExtensionHistoryRows(
              previousSearchExamineesData?.ecSearchTimeExtendedExamEventSessionExaminees
                .extendedExamEventSessionExaminees,
              examEvent.courses,
            )
          : [],
    [searchExamineesData, previousSearchExamineesData, examEvent.courses],
  );

  const totalExaminees =
    searchExamineesData?.ecSearchTimeExtendedExamEventSessionExaminees.total ??
    previousSearchExamineesData?.ecSearchTimeExtendedExamEventSessionExaminees.total ??
    0;

  const handleOnExited = () => {
    setSearchParams({ search: '', page: 0, pageSize: 10 });
    setSelectedSession(null);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth='lg'
      fullWidth
      slotProps={{
        transition: {
          onExited: handleOnExited,
        },
      }}
    >
      <DialogTitle onClose={handleClose}>ประวัติการทดเวลาสอบ</DialogTitle>
      <Divider />
      <DialogContent>
        {adjustedSessionTime ? (
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
            <Box
              sx={(theme) => ({
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: theme.shape.rounded(2),
                p: theme.spacing(4),
                backgroundColor: theme.palette.grey[50],
              })}
            >
              <Text>
                {sessionText}:{' '}
                <Text component='span' variant='body1Semibold'>
                  {formatStartToEndTime(adjustedSessionTime.startedAt, adjustedSessionTime.endedAt)}
                </Text>
              </Text>
            </Box>

            <Alert severity='info' sx={{ alignItems: 'flex-start' }}>
              <Text>
                เหตุผลที่แสดงเป็นเหตุผลของการทดเวลารายคนเท่านั้น — การทดโดยระบบไม่มีการระบุเหตุผล
                ส่วนการทดทั้งรอบจะบันทึกเหตุผลไว้ใน Audit Log
                หากผู้เข้าสอบมีเฉพาะการทดอัตโนมัติหรือทดทั้งรอบ ช่องเหตุผลจะแสดงเป็น “-”
              </Text>
            </Alert>

            <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Text variant='h4'>รายชื่อผู้เข้าสอบทั้งหมด</Text>
              <Box sx={{ width: '300px' }}>
                <SearchInput
                  placeholder='ค้นหาชื่อผู้เข้าสอบ...'
                  debounceTime={700}
                  onChange={handleSearchChange}
                />
              </Box>
            </Flex>

            <TimeExtensionHistoryDataGrid
              rows={examinees}
              loading={isLoading}
              paginationModel={{
                page: searchParams.page,
                pageSize: searchParams.pageSize,
              }}
              slots={{
                noRowsOverlay: () =>
                  searchParams.search ? <NoRowsOverlay /> : <EmptyTimeExtensionHistoryOverlay />,
              }}
              slotProps={{
                footer: {
                  total: totalExaminees,
                  pagination: {
                    page: searchParams.page,
                    count: totalExaminees,
                    rowsPerPage: searchParams.pageSize,
                    onPageChange: (_event, page) => handlePageChange(page),
                    onRowsPerPageChange: (event) =>
                      handlePageSizeChange(Number(event.target.value)),
                  },
                },
              }}
            />
          </Flex>
        ) : (
          <ErrorState />
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button size='large' onClick={handleClose}>
          ปิดหน้าต่าง
        </Button>
      </DialogActions>
    </Dialog>
  );
}
